import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FilterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  // Cascader,
  type FormProps,
  Flex,
  theme,
  Radio,
  DatePicker,
  Typography,
  CascaderProps,
  GetProp,
} from "antd";
import { CrudFilters, useGetIdentity } from "@refinedev/core";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { displayEmail, getStatuses } from "@/utilities";
import {
  CustomersFilter,
  OperatorsFilter,
  parseCustomersFilterValue,
  parseOperatorsFilterValue,
} from "@/components";
import esES from "antd/es/date-picker/locale/es_ES";

const { RangePicker } = DatePicker;
const { Text } = Typography;

type DefaultOptionType = GetProp<CascaderProps, "options">[number];

type Contact = {
  contactId: number;
  isActive: boolean;
  user: { email: string; firstname: string; lastname: string };
  permissions?: {
    store?: { code: string; name?: string }[];
    seller?: { code: string; name?: string }[];
  };
};

export const mapContactsToCascaderOptions = (
  contacts: Contact[],
  t: (k: string) => string,
): DefaultOptionType[] => {
  const groupNode = (
    type: "store" | "seller",
    items?: { code: string; name?: string }[],
  ) =>
    (items && items.length
      ? {
        value: type,
        label:
          type === "store" ? t("permissions.store") : t("permissions.seller"),
        children: items.map((p) => ({
          value: p.code,
          label: p.name || p.code,
        })),
      }
      : null) as unknown as DefaultOptionType | null;

  return (contacts || []).map((c) => {
    const fullName = `${c?.user?.firstname ?? ""} ${c?.user?.lastname ?? ""
      }`.trim();
    const children: DefaultOptionType[] = [];
    const storeGroup = groupNode("store", c.permissions?.store);
    const sellerGroup = groupNode("seller", c.permissions?.seller);
    if (storeGroup) children.push(storeGroup);
    if (sellerGroup) children.push(sellerGroup);

    return {
      value: String(c.contactId),
      label: fullName || displayEmail(c.user.email),
      disabled: c.isActive === false,
      children: children.length ? children : undefined,
    };
  });
};

export const QuotesFilters: React.FC<{
  searchFormProps: FormProps;
  filters?: CrudFilters;
  sellersSelectProps?: any;
  storesSelectProps?: any;
  selectedStores: any;
  handleChangeStores?: any;
  t?: any;
}> = ({
  searchFormProps,
  filters,
  sellersSelectProps,
  storesSelectProps,
  selectedStores,
  handleChangeStores,
  t,
}) => {
    const [calendarValue, setCalendarValue] = useState<any>();
    const [showRangePicker, setShowRangePicker] = useState(false);
    const [calendarValueCreated, setCalendarValueCreated] = useState<any>();
    const [showRangePickerCreated, setShowRangePickerCreated] = useState(false);
    const [radioValue, setRadioValue] = useLocalStorage<any>("RadioCalendar", []);
    const [radioValueCreated, setRadioValueCreated] = useLocalStorage<any>(
      "RadioCalendarCreated",
      [],
    );

    const { data: identity } = useGetIdentity<{
      email?: string;
      contactId?: number;
    }>();

    const [radioValueParticipant, setRadioValueParticipant] = useState<
      "all" | "me"
    >("all");

    // Al abrir el drawer con filtros aplicados que el formulario no conoce (URL, o drawer destruido),
    // se rehidratan desde los ids; los nombres no viajan en la URL.
    useEffect(() => {
      const form = searchFormProps?.form;
      const ops = filters?.find((x: any) => x.field === "operators");
      const actualOps = form?.getFieldValue("operators");
      if (ops?.value && !(Array.isArray(actualOps) && actualOps.length)) {
        form?.setFieldsValue({ operators: parseOperatorsFilterValue(ops.value) });
      }
      const cus = filters?.find((x: any) => x.field === "customers");
      const actualCus = form?.getFieldValue("customers");
      if (cus?.value && !(Array.isArray(actualCus) && actualCus.length)) {
        form?.setFieldsValue({ customers: parseCustomersFilterValue(cus.value) });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const participantPresets = useMemo(
      () => [
        { label: t("filter.participant.all"), value: "all" as const },
        {
          label: t("filter.participant.me"),
          value: "me" as const,
        },
      ],
      [t],
    );

    const handleRadioChangeParticipant = (e: any) => {
      const val = e.target.value as "all" | "me";
      setRadioValueParticipant(val);

      // "Soy participante" filters by the logged-in user's userId, not by the
      // JWT-cached contactId — the latter is 0 when login skips storeCode and
      // misses participations through the user's contacts in other stores.
      // Backend (`quotes.service.ts`) resolves userId → all of the user's
      // contactIds and feeds the existing `pp.contact_id IN (...)` path.
      if (val === "me") {
        searchFormProps.form?.setFieldsValue({
          participant_toggle: (identity as any)?.id,
        });
      } else {
        searchFormProps.form?.setFieldsValue({
          participant_toggle: undefined,
        });
      }
    };

    // "Comprado asistido" es un estado real del presupuesto, pero getStatuses (compartido con Kanban,
    // Contactos y Pedidos) no lo lista: se suma acá para no crearle una columna al Kanban.
    const STATUSES = getStatuses(t).flatMap((s) =>
      s.id === "buy_off"
        ? [s, { id: "bought_assisted", label: t("quotes.status.BoughtAssisted") }]
        : [s],
    );

    const { token } = theme.useToken();
    const handleRangeChange = (dates: null | (Dayjs | null)[]) => {
      if (dates) {
        searchFormProps.form?.setFieldsValue({ calendar: dates });
      } else {
        searchFormProps.form?.setFieldsValue({ calendar: undefined });
      }
    };

    const handleRangeChangeCreated = (dates: null | (Dayjs | null)[]) => {
      if (dates) {
        searchFormProps.form?.setFieldsValue({ calendar_created: dates });
      } else {
        searchFormProps.form?.setFieldsValue({ calendar_created: undefined });
      }
    };

    useEffect(() => {
      // Toggle "Me" is driven by participations.userId now: matches every
      // contact row belonging to the logged-in user, across stores. Sync the
      // radio when the URL/state already carries this filter for the current
      // user (e.g., back-navigation, deep links).
      const myUserId = (identity as any)?.id;
      const f = filters?.find(
        (x: any) => x.field === "participations.userId",
      );
      if (
        f?.value !== undefined &&
        myUserId !== undefined &&
        Number(f.value) === Number(myUserId)
      ) {
        setRadioValueParticipant("me");
        searchFormProps.form?.setFieldsValue({
          participant_toggle: myUserId,
        });
      } else {
        setRadioValueParticipant("all");
        searchFormProps.form?.setFieldsValue({
          participant_toggle: undefined,
        });
      }
    }, [filters, (identity as any)?.id]);

    useEffect(() => {
      const myUserId = (identity as any)?.id;
      if (radioValueParticipant === "me" && myUserId) {
        searchFormProps.form?.setFieldsValue({
          participant_toggle: myUserId,
        });
      }
    }, [radioValueParticipant, (identity as any)?.id]);

    useEffect(() => {
      searchFormProps.form?.setFieldsValue({ calendar: calendarValue });
    }, [calendarValue]);

    useEffect(() => {
      searchFormProps.form?.setFieldsValue({
        calendar_created: calendarValueCreated,
      });
      setShowRangePickerCreated(
        radioValueCreated === "personalized" ? true : false,
      );
      setShowRangePicker(radioValue === "personalized" ? true : false);
    }, [calendarValueCreated]);

    const thisToday: [Dayjs, Dayjs] = [
      dayjs().startOf("day"),
      dayjs().endOf("day"),
    ];

    const thisWeek: [Dayjs, Dayjs] = [
      dayjs().startOf("week"),
      dayjs().endOf("week"),
    ];
    const thisMonth: [Dayjs, Dayjs] = [
      dayjs().startOf("month"),
      dayjs().endOf("month"),
    ];

    const rangePresets = useMemo(
      () => [
        { label: t("filter.calendar.all"), value: "" },
        { label: t("filter.calendar.today"), value: "today" },
        { label: t("filter.calendar.thisWeek"), value: "thisweek" },
        { label: t("filter.calendar.thisMonth"), value: "thismonth" },
        { label: t("filter.calendar.personalized"), value: "personalized" },
      ],
      [t],
    );

    useEffect(() => {
      if (filters) {
        /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
        const calendarFilter = filters?.find((f) => f.field === "updatedAt");
        const calendarFilterCreated = filters?.find(
          /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
          (f) => f.field === "createdAt",
        );

        if (calendarFilter) {
          const [start, end] = calendarFilter.value || [];
          if (start && end) {
            setCalendarValue([dayjs(start), dayjs(end)]);
          } else {
            setCalendarValue(undefined);
            setRadioValue("");
            setShowRangePicker(false);
          }
        } else {
          setCalendarValue(undefined);
          setRadioValue("");
          setShowRangePicker(false);
        }

        if (calendarFilterCreated) {
          const [start, end] = calendarFilterCreated.value || [];
          if (start && end) {
            setCalendarValueCreated([dayjs(start), dayjs(end)]);
          } else {
            setCalendarValueCreated(undefined);
            setRadioValueCreated("");
            setShowRangePickerCreated(false);
          }
        } else {
          setCalendarValueCreated(undefined);
          setRadioValueCreated("");
          setShowRangePickerCreated(false);
        }
      }
    }, [filters]);

    const handleRadioChange = useCallback(
      (e: any) => {
        const { value } = e.target;
        setRadioValue(value);

        if (value === "personalized") {
          setShowRangePicker(true);
          setCalendarValue(value);
        } else if (value === "today") {
          setShowRangePicker(false);
          setCalendarValue(thisToday);
        } else if (value === "thisweek") {
          setShowRangePicker(false);
          setCalendarValue(thisWeek);
        } else if (value === "thismonth") {
          setShowRangePicker(false);
          setCalendarValue(thisMonth);
        } else {
          setShowRangePicker(false);
          setCalendarValue(undefined);
        }
      },
      [setRadioValue, setShowRangePicker, setCalendarValue],
    );

    const handleRadioChangeCreated = useCallback(
      (e: any) => {
        const { value } = e.target;
        setRadioValueCreated(value);

        if (value === "personalized") {
          setShowRangePickerCreated(true);
          setCalendarValueCreated(value);
        } else if (value === "today") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisToday);
        } else if (value === "thisweek") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisWeek);
        } else if (value === "thismonth") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisMonth);
        } else {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(undefined);
        }
      },
      [setRadioValueCreated, setShowRangePickerCreated, setCalendarValueCreated],
    );

    function clearFilters() {
      searchFormProps.form?.resetFields();
      setCalendarValue(undefined);
      setCalendarValueCreated(undefined);
      setRadioValue("");
      setRadioValueCreated("");
      setShowRangePicker(false);
      setShowRangePickerCreated(false);

      setRadioValueParticipant("all");
      searchFormProps.form?.setFieldsValue({
        participant_toggle: undefined,
      });
      searchFormProps.form?.submit();
    }


    // El Cascader de contactos quedó comentado más abajo; su andamiaje (filtro, onChange,
    // displayRender) se fue con la Fase 7 (xcons-docs#308), que lo reemplaza por los componentes
    // de Operadores y Clientes.

    return (
      <Form
        layout="vertical"
        form={searchFormProps?.form}
        onFinish={searchFormProps?.onFinish}
        style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
      >
        <div
          key={JSON.stringify(filters ?? [])}
          style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}
        >
        <Form.Item name="participant_toggle" hidden>
          <Input />
        </Form.Item>
        {/* Hidden field used by FilterTags to render the contact's name
            instead of the numeric contactId. Set in the Select onChange. */}
        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.participant.title")}
            </span>
          }
        >
          <Flex vertical>
            <Radio.Group
              value={radioValueParticipant}
              style={{ display: "flex", gap: 10 }}
              size="small"
              onChange={handleRadioChangeParticipant}
            >
              {participantPresets.map((preset, i) => (
                <Radio.Button key={`${i}-participant`} value={preset.value}>
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.calendar.lastCreate")}
            </span>
          }
          name="calendar_created"
          key="calendar_created_unique"
        >
          <Flex vertical>
            <Radio.Group
              value={radioValueCreated}
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: showRangePickerCreated ? "10px" : "",
              }}
              size="small"
              onChange={handleRadioChangeCreated}
            >
              {rangePresets.map((preset, i) => (
                <Radio.Button
                  key={`${i}-created`}
                  value={preset.value}
                  name={preset.value}
                >
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
            {showRangePickerCreated && (
              <RangePicker locale={esES} onChange={handleRangeChangeCreated} required />
            )}
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.calendar.lastUpdate")}
            </span>
          }
          name="calendar"
          key="calendar_updated"
        >
          <Flex vertical>
            <Radio.Group
              value={radioValue}
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: showRangePicker ? "10px" : "",
              }}
              size="small"
              onChange={handleRadioChange}
            >
              {rangePresets.map((preset, i) => (
                <Radio.Button key={i} value={preset.value} name={preset.value}>
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
            {showRangePicker && (
              <RangePicker onChange={handleRangeChange} required />
            )}
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("menu.label.sellers")}
            </span>
          }
          name="sellerCode"
        >
          <Flex vertical>
            <Select
              allowClear
              mode="multiple"
              placeholder={t("filter.calendar.all")}
              options={sellersSelectProps?.map((option: any) => ({
                label: option.name as string,
                value: option.code as string,
              }))}
              defaultValue={
                // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
                filters?.find((f) => f.field === "quoteSellers.sellerCode")?.value
              }
              onChange={(value) => {
                searchFormProps.form?.setFieldsValue({ sellerCode: value });
              }}
            />
          </Flex>
        </Form.Item>

        {/* Fase 7 de filtros-operadores-clientes (xcons-docs#308): Operadores por afiliación y
            Clientes en lista, ambos acotados al store elegido arriba en cada render. */}
        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.operators.title")}
            </span>
          }
          name="operators"
        >
          <OperatorsFilter
            storeCodes={(selectedStores ?? [])
              .map((s: any) => s?.code)
              .filter(Boolean)}
          />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.customers.title")}
            </span>
          }
          name="customers"
        >
          <CustomersFilter
            storeCodes={(selectedStores ?? [])
              .map((s: any) => s?.code)
              .filter(Boolean)}
          />
        </Form.Item>

        {/* <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("menu.label.contacts")}
          </span>
        }
        name="contactRelation"
      >
        <Flex vertical>
          <Cascader
            showSearch={{ filter }}
            onSearch={onSearchCascader}
            changeOnSelect
            allowClear
            placeholder={t("quotes.contacts.search")}
            options={mapContactsToCascaderOptions(contacts ?? [], t)}
            onChange={onCascaderChange}
            // útil si querés ver la ruta completa
            displayRender={(labels) => labels.join(" / ")}
            // loading UX similar al Select:
            notFoundContent={
              loadingContacts ? (
                <Flex justify="center" align="center" style={{ padding: 16 }}>
                  <Spin size="small" />
                </Flex>
              ) : undefined
            }
          />
        </Flex>
      </Form.Item> */}

        <Flex gap={8}>
          <Form.Item
            label={
              <span style={{ color: token.colorTextTertiary }}>
                {t("filter.products.labelPrice")}
              </span>
            }
            name="price_product_from"
          >
            <InputNumber<number>
              addonBefore="$"
              min={0}
              style={{ width: "100%" }}
              placeholder={t("filter.products.labelPriceFrom")}
            />
          </Form.Item>
          <Form.Item
            label={
              <span style={{ color: "white" }}>
                {t("filter.products.labelPrice")}
              </span>
            }
            name="price_product_to"
          >
            <InputNumber<number>
              addonBefore="$"
              min={0}
              style={{ width: "100%" }}
              placeholder={t("filter.products.labelPriceTo")}
            />
          </Form.Item>
        </Flex>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("create.seller.labelState")}
            </span>
          }
          name="region"
        >
          <Flex vertical>
            {/* <Select
            allowClear
            placeholder={t("filter.calendar.all")}
            defaultValue={
              // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
              filters?.find((f) => f.field === "product.status")?.value
            }
            onChange={(value) => {
              searchFormProps.form?.setFieldsValue({ states: value });
            }}
          /> */}
            <Input
              type="text"
              defaultValue={searchFormProps.form?.getFieldValue("region")}
            />
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("create.seller.labelCity")}
            </span>
          }
          name="city"
        >
          <Flex vertical>
            {/* <Select
            allowClear
            placeholder={t("filter.calendar.all")}
            defaultValue={
              // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
              filters?.find((f) => f.field === "product.status")?.value
            }
            onChange={(value) => {
              searchFormProps.form?.setFieldsValue({ city: value });
            }}
          /> */}
            <Input
              type="text"
              defaultValue={searchFormProps.form?.getFieldValue("city")}
            />
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("posts.fields.status.title")}
            </span>
          }
          name="status"
        >
          <Flex vertical>
            <Select
              allowClear
              placeholder={t("filter.calendar.all")}
              options={STATUSES?.map((option: any) => ({
                label: option.label,
                value: option.id,
              }))}
              defaultValue={(() => {
                const raw = filters?.find(
                  // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
                  (f) => f.field === "quoteSellers.state",
                )?.value;
                // El filtro "Store" viaja como ["quoting", "store"] (operator "in");
                // el Select es single-value, así que se rehidrata al id que representa
                // la opción, no al array crudo.
                return Array.isArray(raw) ? raw[0] : raw;
              })()}
              onChange={(value) => {
                searchFormProps.form?.setFieldsValue({ status: value });
              }}
            />
          </Flex>
        </Form.Item>

        </div>
        <Flex
          align="center"
          justify="end"
          gap="8px"
          style={{ flexShrink: 0, padding: "12px 24px", borderTop: "1px solid #f0f0f0", backgroundColor: "#fff" }}
        >
          <Button type="default" danger onClick={clearFilters}>
            {t("filter.button.clearFilters")}
          </Button>
          <Button htmlType="submit" type="primary" icon={<FilterOutlined />}>
            {t("filter.button.filter")}
          </Button>
        </Flex>
      </Form>
    );
  };